import { Container, Graphics } from 'pixi.js';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { registerScenery, synthCtx } from '../scenery';

// ── EDITABLE SCENERY REGISTRATIONS (passive metadata for the level editor) ──────────────────────────
// Each bespoke open-space FURNITURE piece is registered under a stable kind so the editor can re-render /
// add it. All these builders take (ctx) and use the synthetic origin-anchored context. The three desk
// clutter pieces (mug / papers / desk plant) are placed via place() inside workstation(), each a distinct
// kind. Painted/architectural BACKDROP (the pinboard feature-wall art, rugs, glows, wall art) is NOT
// registered.
registerScenery('biophilic-island', (art) => biophilicIsland(synthCtx(art)), 'Biophilic island', '🪴');
registerScenery('coffee-counter', (art) => coffeeCounter(synthCtx(art)), 'Coffee counter', '☕');
registerScenery('water-cooler', (art) => waterCooler(synthCtx(art)), 'Water cooler', '🚰');
registerScenery('desk-mug', (art) => mug(synthCtx(art)), 'Desk mug', '☕');
registerScenery('desk-papers', (art) => papers(synthCtx(art)), 'Desk papers', '📄');
registerScenery('desk-plant', (art) => deskPlant(synthCtx(art)), 'Desk plant', '🪴');

/**
 * OPEN SPACE — the central open-plan HUB, the office's dominant room and busiest crossroads. It is the
 * first thing you see and the heart everything else orbits, so it is dressed to be STRIKING and FULL,
 * built around ONE bold central green statement — a composed centerpiece, not a desk grid floating in
 * empty floor, and never a bare middle.
 *
 * Geometry (zone x[440..1120] y[280..840], interior 440..1120 × 280..840, centre 780,560). FIVE doors,
 * each kept on a wide clear lane:
 *   - N edge x[680..880] → kitchen      |  S edge x[680..880] → reception   (one vertical N↔S corridor)
 *   - W edge y[350..510] → lounge        |  E edge y[350..510] → ceo office
 *   - E edge y[640..780] → toilet (lower-right approach)
 *
 * ── THE COMPOSITION: a HERO green centrepiece anchoring the room, the desk spine above it, balanced
 *    seating zones to either flank — full but ORDERED, with clean wide lanes through all five doors ────
 *   HERO · STATEMENT BIOPHILIC ISLAND (the room's CENTRAL focal point, centred ~660,690) — a big raised
 *     circular planter brimming with layered greenery, ringed by a continuous curved wooden BENCH you
 *     perch on from any side, grounded on its own neat round hero rug. It sits in the visual heart of
 *     the lower room (just WEST of the N↔S corridor so that lane stays open), filling what used to be
 *     dead-centre floor with a living sculpture people gather around. FOUR bench seats are SITTABLE
 *     (N/E/S/W of the green centre), all facing inward at the foliage — the "wow" piece of the room.
 *
 *   SPINE — the 4-DESK BULLPEN POD across the upper-middle (y≈540), drawn AT the gameplay anchors
 *     (620,540)(740,540)(860,540)(980,540) each facing UP, tightened on ONE shared hero rug so the row
 *     reads as a single tidy pod lined up with the interactive workstation prop at (780,540). The N↔S
 *     corridor flows right through it (ducks sit INTO the desks, they don't block the lane); the hero
 *     island sits directly below it so the centre column reads desks-over-garden, never empty.
 *
 *   BREAKOUT LOUNGE (lower-RIGHT, ~975,724, clear of the toilet approach) — a generous, real lounge: a
 *     sofa + two armchairs around a coffee table on a neat rug, the office's relax-and-chat corner.
 *     Sofa + both armchairs are all SITTABLE.
 *
 *   FEATURE WALL (upper / N back wall) — a big planning whiteboard + a bustling pin wall flanking the
 *     kitchen door, with framed art, so the top band reads as a deliberate collaboration backdrop the
 *     whole room faces (not dead space above the desks).
 *
 * Deliberate standing accents (a water cooler + coffee counter station, a tall bookshelf, a corner lamp,
 * framed art on the solid wall runs) and exactly FOUR arranged plants frame the zones + corners. Nothing
 * floats unanchored; the centre is full; every door lane stays wide open.
 *
 * Built ONCE on map build/re-skin (never per frame): all geometry is static, themed off `art` so it
 * re-skins per world theme, and composed from the shared furniture catalog plus small inline builders.
 */

/** A small mug — a warm, human touch left on a desk (steam hint on its rim). */
function mug(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const body = mix(0xffffff, art.accent, 0.3);
  c.addChild(
    new Graphics()
      .ellipse(0, 4, 6, 2.4)
      .fill({ color: 0x000000, alpha: 0.18 })
      .roundRect(-4, -4, 8, 9, 2)
      .fill(body)
      .ellipse(0, -4, 4, 1.6)
      .fill(darken(body, 0.18))
      .arc(5, -1, 3, -1.2, 1.2)
      .stroke({ color: darken(body, 0.2), width: 1.4 })
      .ellipse(0, -4, 2.4, 1)
      .fill({ color: lighten(art.glow, 0.2), alpha: 0.6 }),
  );
  return c;
}

/** A loose stack of papers — the lived-in scatter of real work on a desk. */
function papers(ctx: RoomContext): Container {
  const { darken } = ctx;
  const c = new Container();
  const sheet = 0xf4f1ea;
  c.addChild(
    new Graphics()
      .ellipse(0, 3, 9, 2.4)
      .fill({ color: 0x000000, alpha: 0.14 })
      .roundRect(-8, -5, 15, 11, 1.5)
      .fill(darken(sheet, 0.1))
      .roundRect(-7, -7, 15, 11, 1.5)
      .fill(sheet)
      .rect(-4, -4, 9, 1)
      .fill({ color: 0xb9b3a6, alpha: 0.8 })
      .rect(-4, -1, 7, 1)
      .fill({ color: 0xb9b3a6, alpha: 0.7 }),
  );
  return c;
}

/** A tiny succulent in a cup — the desk plant every engineer keeps alive (barely). */
function deskPlant(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const leaf = mix(0x3f9d52, art.accent, 0.12);
  const pot = mix(0xc97f4a, art.wall, 0.2);
  c.addChild(
    new Graphics()
      .ellipse(0, 5, 6, 2.2)
      .fill({ color: 0x000000, alpha: 0.16 })
      .rect(-4, 0, 8, 6)
      .fill(pot)
      .rect(-4, 0, 8, 1.6)
      .fill({ color: 0xffffff, alpha: 0.14 })
      .ellipse(0, -3, 4, 6)
      .fill(leaf)
      .ellipse(-3, -1, 3, 4)
      .fill(lighten(leaf, 0.1))
      .ellipse(3, -1, 3, 4)
      .fill(darken(leaf, 0.12)),
  );
  return c;
}

/**
 * ONE WORKSTATION at a bullpen anchor: a long desk facing UP (monitor/computer at the back edge, a task
 * chair pulled in below it) with a sprinkle of human clutter. `tower` swaps the monitor for the full
 * tower-and-screen so adjacent desks in the row don't look stamped. Built around the anchor (px,py); the
 * footprint is ~96×60 (desk + chair + screen) and the catalog pieces carry their own contact shadows.
 */
function workstation(ctx: RoomContext, px: number, py: number, tower: boolean, clutter: 'mug' | 'papers' | 'plant'): void {
  const { f } = ctx;
  // Screen at the back (top), desk slab, then the chair pulled in below — the whole thing faces UP.
  place(ctx, tower ? f.computer(ctx.art) : f.monitor(ctx.art), px, py - 12);
  place(ctx, f.longDesk(ctx.art, 96), px, py + 2);
  place(ctx, f.deskChair(ctx.art, 'up'), px, py + 26);
  // A little human clutter on the desktop, offset to the side so it never hides the screen.
  const piece = clutter === 'mug' ? mug(ctx) : clutter === 'papers' ? papers(ctx) : deskPlant(ctx);
  const clutterKind = clutter === 'mug' ? 'desk-mug' : clutter === 'papers' ? 'desk-papers' : 'desk-plant';
  place(ctx, piece, px + 30, py - 2, clutterKind);
}

/**
 * THE STATEMENT BIOPHILIC ISLAND — the room's HERO focal point and central anchor. A big circular raised
 * stone-and-wood planter brimming with layered foliage (a generous indoor garden), wrapped by a
 * CONTINUOUS curved wooden bench so it reads as a place people gather and perch around from every side.
 * Origin centred on the island; carries its own soft floor shadow. Footprint ≈ 184×150 (the round hero
 * rug under it is placed separately, sized just larger). Deliberately bolder than a corner plant so the
 * eye lands HERE, dead centre of the lower room — the "wow" piece.
 */
function biophilicIsland(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const wood = mix(0x6a4a2b, art.wall, 0.3);
  const stone = mix(0xb9b0a2, art.wall, 0.35);
  const soil = darken(0x4a3526, 0.1);
  const leaf = mix(0x3f9d52, art.accent, 0.12);

  // Big soft floor shadow grounding the whole island.
  c.addChild(new Graphics().ellipse(0, 38, 92, 26).fill({ color: 0x000000, alpha: 0.2 }));

  // The continuous curved wooden BENCH ring wrapping the planter (drawn first so the planter overlaps its
  // front), reading as one perch you can sit on from any side facing the green centre.
  c.addChild(
    new Graphics()
      // outer bench band
      .ellipse(0, 8, 88, 52)
      .fill(darken(wood, 0.2))
      .ellipse(0, 3, 88, 50)
      .fill(wood)
      .ellipse(0, 0, 88, 48)
      .fill({ color: 0xffffff, alpha: 0.08 })
      // inner cut so it reads as a ring of seating, not a solid disc
      .ellipse(0, 3, 64, 36)
      .fill(darken(wood, 0.14)),
  );
  // Slat seams radiating across the bench top (short arcs hint at planks all the way round).
  const slats = new Graphics();
  for (const a of [-1.25, -0.85, -0.42, 0, 0.42, 0.85, 1.25, Math.PI]) {
    const ox = Math.cos(a) * 78;
    const oy = 6 + Math.sin(a) * 42;
    const ix = Math.cos(a) * 66;
    const iy = 6 + Math.sin(a) * 34;
    slats.moveTo(ix, iy).lineTo(ox, oy);
  }
  slats.stroke({ color: darken(wood, 0.3), width: 1.4, alpha: 0.6 });
  c.addChild(slats);

  // The raised PLANTER box (stone rim) sitting inside the bench ring.
  c.addChild(
    new Graphics()
      .ellipse(0, -2, 56, 32)
      .fill(darken(stone, 0.18))
      .ellipse(0, -7, 56, 32)
      .fill(stone)
      .ellipse(0, -9, 56, 30)
      .fill({ color: 0xffffff, alpha: 0.1 })
      // soil bed inside the rim
      .ellipse(0, -9, 47, 22)
      .fill(soil),
  );

  // Layered FOLIAGE bursting from the soil — back to front, light to dark, so it reads lush and full and
  // tall enough to be the room's tallest indoor mass (the hero silhouette).
  const foliage = new Graphics();
  // tall back fronds
  for (const [x, h, t] of [
    [-30, 54, 0.0],
    [-12, 70, 0.1],
    [4, 76, -0.04],
    [18, 64, -0.08],
    [34, 50, 0.12],
  ] as const) {
    foliage.ellipse(x, -20 - h / 2, 8, h / 2).fill(t >= 0 ? darken(leaf, t) : lighten(leaf, -t));
  }
  // mid broad leaves
  for (const [x, y, rx, ry, tone] of [
    [-38, -16, 16, 24, lighten(leaf, 0.12)],
    [38, -14, 16, 24, darken(leaf, 0.1)],
    [-16, -6, 19, 22, lighten(leaf, 0.06)],
    [20, -8, 18, 22, leaf],
    [0, -2, 16, 18, lighten(leaf, 0.1)],
  ] as const) {
    foliage.ellipse(x, y, rx, ry).fill(tone);
  }
  // a few bright accent blooms for a pop of colour (sparing — most foliage stays green).
  foliage
    .circle(-6, -28, 4.5)
    .fill({ color: lighten(art.accent, 0.25), alpha: 0.95 })
    .circle(22, -22, 4)
    .fill({ color: 0xffd23f, alpha: 0.9 })
    .circle(-26, -12, 3.5)
    .fill({ color: 0xff9db4, alpha: 0.9 })
    .circle(10, -16, 3)
    .fill({ color: lighten(art.glow, 0.15), alpha: 0.85 });
  c.addChild(foliage);

  return c;
}

/**
 * A WATER-COOLER: the classic break-spot bottle-on-a-stand — a believable commons detail people gather at.
 * Origin at its base; the cooler body rises above it with its own contact shadow.
 */
function waterCooler(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const body = mix(0xe8edf2, art.wall, 0.18); // cool white appliance
  const water = mix(0x6fc6ef, art.accent, 0.15);
  c.addChild(new Graphics().ellipse(0, 18, 16, 6).fill({ color: 0x000000, alpha: 0.2 }));
  c.addChild(
    new Graphics()
      // cabinet
      .roundRect(-13, -8, 26, 28, 4)
      .fill(body)
      .roundRect(-13, -8, 26, 6, 4)
      .fill({ color: 0xffffff, alpha: 0.22 })
      // two little taps (hot/cold)
      .roundRect(-7, 4, 5, 4, 1.5)
      .fill(darken(body, 0.3))
      .roundRect(2, 4, 5, 4, 1.5)
      .fill({ color: 0xd9534f, alpha: 0.85 })
      // a catch tray
      .rect(-9, 12, 18, 2)
      .fill(darken(body, 0.22))
      // the inverted water bottle on top
      .roundRect(-10, -30, 20, 24, 6)
      .fill({ color: water, alpha: 0.85 })
      .roundRect(-10, -30, 20, 8, 6)
      .fill({ color: lighten(water, 0.25), alpha: 0.6 })
      .rect(-5, -34, 10, 5)
      .fill(darken(body, 0.1)),
  );
  return c;
}

/**
 * A COFFEE COUNTER: a slim wood counter with a worktop, a glowing little machine and a mug — a believable
 * stand-and-refill station. Origin centred on its body; carries its own contact shadow.
 */
function coffeeCounter(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const wood = mix(0x6a4a2b, art.wall, 0.3);
  const top = lighten(wood, 0.18);
  c.addChild(new Graphics().ellipse(0, 22, 60, 10).fill({ color: 0x000000, alpha: 0.2 }));
  c.addChild(
    new Graphics()
      // cabinet body + worktop lip
      .roundRect(-56, -6, 112, 28, 4)
      .fill(wood)
      .roundRect(-58, -12, 116, 8, 4)
      .fill(top)
      .roundRect(-58, -12, 116, 3, 4)
      .fill({ color: 0xffffff, alpha: 0.16 })
      // cabinet door seams
      .moveTo(-19, -4)
      .lineTo(-19, 20)
      .moveTo(19, -4)
      .lineTo(19, 20)
      .stroke({ color: darken(wood, 0.22), width: 1.6, alpha: 0.7 }),
  );
  // A little espresso machine on the left of the worktop.
  const machine = mix(0xe8edf2, art.wall, 0.18);
  c.addChild(
    new Graphics()
      .roundRect(-40, -30, 20, 20, 3)
      .fill(machine)
      .roundRect(-40, -30, 20, 5, 3)
      .fill({ color: 0xffffff, alpha: 0.22 })
      .rect(-34, -10, 8, 3)
      .fill(darken(machine, 0.3))
      .circle(-30, -22, 2.2)
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.9 }),
  );
  // A carafe + steaming mug on the right of the worktop.
  c.addChild(
    new Graphics()
      .roundRect(24, -26, 12, 16, 2)
      .fill({ color: darken(art.accent, 0.1), alpha: 0.85 })
      .roundRect(24, -26, 12, 4, 2)
      .fill({ color: 0xffffff, alpha: 0.2 })
      .roundRect(40, -16, 8, 7, 2)
      .fill(lighten(wood, 0.3))
      .ellipse(44, -18, 2, 1)
      .fill({ color: lighten(art.glow, 0.2), alpha: 0.5 }),
  );
  return c;
}

/**
 * A PINBOARD: a cork board on the wall scattered with little colour notes — the feature wall's living
 * memory. Hugs the wall (flat, no contact shadow). Centred on its placement point.
 */
function pinboard(ctx: RoomContext): Container {
  const { art, mix, darken, lighten } = ctx;
  const c = new Container();
  const cork = mix(0xc79a5b, art.wall, 0.3);
  const g = new Graphics()
    .roundRect(-30, -20, 60, 40, 3)
    .fill(darken(cork, 0.2))
    .roundRect(-27, -17, 54, 34, 2)
    .fill(cork);
  // A scatter of pinned sticky notes with a tiny pin dot each.
  const notes: ReadonlyArray<readonly [number, number, number]> = [
    [-18, -9, 0xffd23f],
    [-2, -11, 0x95e06c],
    [13, -7, 0xff9db4],
    [-14, 6, 0x5ad1ff],
    [4, 5, lighten(art.accent, 0.2)],
    [18, 7, 0xffb86b],
  ];
  for (const [nx, ny, col] of notes) {
    g.roundRect(nx - 7, ny - 6, 14, 13, 1.5)
      .fill({ color: col, alpha: 0.92 })
      .roundRect(nx - 7, ny - 6, 14, 3, 1.5)
      .fill({ color: 0xffffff, alpha: 0.18 })
      .circle(nx, ny - 4, 1.2)
      .fill({ color: darken(col, 0.35), alpha: 0.9 });
  }
  c.addChild(g);
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { container, art, f, lighten } = ctx;
  // Inner wall faces sit ~12px in (WALL_THICKNESS); solid bodies stay ≥16px inside that, and the five
  // door lanes stay wide open: vertical corridor x≈680–880 (N↔S), upper band y≈350–510 (W/E), lower-right
  // approach y≈640–780 (toilet on the E wall).

  // ── SPINE: the 4-desk BULLPEN POD across the middle (y≈540), drawn AT the gameplay anchors so each
  // desk is a real "sit here and work" spot lined up with the interactive workstation prop at (780,540).
  // ONE tightened shared rug under the row ties the pod together (drawn first → underneath). ────────────
  place(ctx, f.rug(art, 500, 132), 800, 540);
  workstation(ctx, 620, 540, false, 'mug'); // far-left desk (monitor)
  workstation(ctx, 740, 540, true, 'papers'); // left-centre desk (tower)
  workstation(ctx, 860, 540, false, 'plant'); // right-centre desk (monitor)
  workstation(ctx, 980, 540, true, 'mug'); // far-right desk (tower)

  // ── HERO · STATEMENT BIOPHILIC ISLAND, the room's CENTRAL anchor (~600,692) — a big round hero rug
  // under a tall greenery-filled planter wrapped by a continuous curved bench you perch on from any side.
  // Sits in the visual heart of the lower room, directly below the desk spine, filling what used to be
  // dead-centre floor. Held just WEST of the N↔S corridor (island/rug right edge ≈688..690 grazes x680 at
  // floor level only; bench SEATS all stay clear of the lane). FOUR bench seats are SITTABLE (N/E/S/W of
  // the green centre), each facing inward at the foliage. ────────────────────────────────────────────────
  const islandGlow = new Graphics().ellipse(596, 700, 118, 110).fill({ color: lighten(art.glow, 0.1), alpha: 0.07 });
  container.addChild(islandGlow); // a soft warm halo under the hero, drawn first so the rug + island sit over it
  place(ctx, f.rug(art, 210, 186), 600, 700); // round-ish hero rug grounding the central island
  place(ctx, biophilicIsland(ctx), 600, 692, 'biophilic-island'); // the statement greenery sculpture (the room's focal point)
  // (Bench seat anchors reported separately: four perches ring the green centre, facing inward.)

  // ── BREAKOUT LOUNGE in the lower-RIGHT (~975,724) — a real lounge: a sofa + two armchairs around a
  // coffee table on a neat rug, the office's relax-and-chat corner balancing the central hero island.
  // Lives EAST of the desk row and WEST of the toilet approach (E lane y640–780 hugs x≈1040–1104), so
  // that lane stays clear. Sofa faces up into the room; the two armchairs flank the coffee table facing
  // the sofa. All SITTABLE. ─────────────────────────────────────────────────────────────────────────────
  place(ctx, f.rug(art, 218, 176), 968, 724); // neat rug sizing the lounge group
  place(ctx, f.sofa(art), 968, 770); // sofa against the lower run, facing up
  place(ctx, f.coffeeTable(art), 968, 718); // low table centred in the group
  place(ctx, f.armchair(art), 902, 700); // left armchair, angled toward the sofa
  place(ctx, f.armchair(art), 1022, 700); // right armchair, angled toward the sofa (body clear of the E2 lane)

  // ── FEATURE WALL on the solid N (back) runs flanking the kitchen door (x680–880): a big planning
  // whiteboard + a bustling pin wall, with framed art, so the top band reads as a collaboration backdrop
  // the whole room faces. Wall-mounted pieces hang flush on the N wall, clear of the kitchen door lane. ─
  place(ctx, f.whiteboard(art), 575, 308); // big planning whiteboard, left N-wall run
  place(ctx, pinboard(ctx), 985, 308); // bustling pin wall, right N-wall run
  place(ctx, f.wallArt(art, 'abstract'), 658, 308); // framed art beside the whiteboard (right edge ≈680, off the door lane)
  place(ctx, f.wallArt(art, 'poster'), 902, 308); // framed art beside the pin wall (left edge ≈880, off the door lane)

  // ── STANDING ACCENTS — a coffee/water refill station + a tall bookshelf, deliberate and tidy. ─────────
  // Coffee + water station freestanding in the upper-right quadrant (right of the corridor, above the
  // desks, left of the E-wall door strip x≳1050 so the E entrance lane stays open).
  place(ctx, coffeeCounter(ctx), 965, 440, 'coffee-counter'); // coffee counter (right edge ≈1023, clear of the E lane)
  place(ctx, waterCooler(ctx), 1035, 426, 'water-cooler'); // water cooler at its right end (right edge ≈1048, clear of the E lane)
  // A tall bookshelf on the W wall in the solid run BELOW the W door (y510–640), balancing the station.
  place(ctx, f.bookshelf(art), 476, 575); // tall shelves on the W run, below the W door

  // ── Framed art breaking up the lower (S) wall, either side of the S corridor. ─────────────────────────
  place(ctx, f.wallArt(art, 'painting'), 520, 818); // SW lower wall
  place(ctx, f.wallArt(art, 'poster'), 1040, 818); // SE lower wall

  // ── FOUR arranged PLANTS framing the zones + corners (deliberate, not scattered) — two flank the
  // central hero island (W approach + SW corner) so the lower-left never reads empty, two frame the
  // right-side lounge + station so the eye reads a balanced ring of greenery around the room. ───────────
  place(ctx, f.plant(art, 'leafy'), 472, 324); // NW corner run (above the W/lounge door), framing the upper-left
  place(ctx, f.plant(art, 'fern'), 498, 800); // SW corner on the floor, anchoring the lower-left below the island
  place(ctx, f.plant(art, 'tall'), 1000, 660); // anchors the lounge's open NW edge on the floor (clear of the E2 lane)
  place(ctx, f.plant(art, 'tall'), 1000, 360); // upper-RIGHT pocket framing the station (right of the corridor, off the E door lane)

  // ── A warm standing LAMP pooling light over the lounge corner, and a faint anchor glow under the
  // interactive workstation prop centre (780,540), kept subtle so neither fights the props or the lane.
  // (The hero island carries its own soft warm halo, added under it in the HERO block above.) ────────────
  place(ctx, f.lamp(art), 1010, 800); // warm light over the lounge (body clear of the E2 wall strip)
  const anchor = new Graphics()
    .roundRect(780 - 26, 540 - 6, 52, 30, 10)
    .fill({ color: lighten(art.glow, 0.05), alpha: 0.08 });
  container.addChild(anchor);
};

export default decorate;
